/**
 * Deployment-partitioned auth storage (P-COND-04256DDD1E68, follow-up of
 * P-COND-EB8B865DFCCA / bug-patterns #784).
 *
 * Every deployment on myconductor.net (prod `/conductor`, test slots
 * `/conductor_test_<slug>`, preview) shares ONE origin, therefore ONE
 * localStorage. Un-partitioned keys let a test-slot login overwrite the
 * production token (own SECRET_KEY) and vice versa. This module is the SINGLE
 * place that spells the auth localStorage keys; every call site goes through
 * `getAuthItem` / `setAuthItem` / `removeAuthItem` / `clearAuthStorage`.
 *
 * Option A: prod (`/conductor`) and the root-based cn build (`/`) keep the
 * historical un-suffixed key names, so existing sessions survive the rollout
 * with no logout and no migration. Any other prefix gets `<base>@<prefix>`.
 *
 * Deliberately imports nothing from services/contexts (no cycles).
 */

/** Production (.net) API prefix — the only definition site. */
export const PROD_API_PREFIX = '/conductor';

/** The six localStorage auth keys (base names, before partitioning). */
export const AUTH_STORAGE_BASE_KEYS = [
  'conductor_auth_token',
  'conductor_user_id',
  'conductor_is_platform_admin',
  'conductor_is_admin',
  'conductor_username',
  'conductor_display_name',
] as const;

export type AuthStorageBaseKey = typeof AUTH_STORAGE_BASE_KEYS[number];

/**
 * This build's API prefix (`/conductor`, `/conductor_test_<slug>`,
 * `/conductor-preview`; `/` on the root-based cn build). Same source as
 * `agentStream.ts::openAgentStream`'s SSE base URL. Trailing slashes are
 * normalized away. Computed per call — tests mutate `process.env`.
 */
export const authPathPrefix = (): string => {
  const prefix = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  return prefix.startsWith('/') ? prefix : '/';
};

/**
 * Deployment-scoped localStorage key for `base`: unchanged for `/` and
 * `PROD_API_PREFIX`, otherwise `${base}@${prefix}`.
 */
export const authStorageKey = (base: AuthStorageBaseKey): string => {
  const prefix = authPathPrefix();
  if (prefix === '/' || prefix === PROD_API_PREFIX) {
    return base;
  }
  return `${base}@${prefix}`;
};

/** Read an auth item; tolerates non-browser / storage-less environments. */
export const getAuthItem = (base: AuthStorageBaseKey): string | null => {
  try {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(authStorageKey(base))
      : null;
  } catch {
    return null;
  }
};

export const setAuthItem = (base: AuthStorageBaseKey, value: string): void => {
  localStorage.setItem(authStorageKey(base), value);
};

export const removeAuthItem = (base: AuthStorageBaseKey): void => {
  localStorage.removeItem(authStorageKey(base));
};

/** Remove all six auth keys for THIS deployment only. */
export const clearAuthStorage = (): void => {
  AUTH_STORAGE_BASE_KEYS.forEach(removeAuthItem);
};
